import { useResizable } from 'hooks/useResizable';
import {
  COMPACT_HEIGHTS,
  COMPACT_MINIMUM_PICKLISTS_HEIGHT,
  DEFAULT_HEIGHTS,
  DEFAULT_MINIMUM_PICKLISTS_HEIGHT,
  DEFAULT_RIGHT_DRAWER_TABS,
  ON_LOAD_TEMPLATE_DEBOUNCE,
  RIGHT_DRAWER_FILTER_HEIGHT,
} from './constants';

import { useCompactUI } from 'hooks/useCompactUI';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';

import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { RightDrawerResizableSection } from './RightDrawerResizableSection';
import { Stack } from 'common/ui/Layout';
import { RightDrawerTab } from './types';
import type {
  RightDrawerPreviewItemType,
  RightDrawerTabType,
  RightDrawerTableItemType,
} from './types';
import { useDebouncedCallback } from 'use-debounce';
import { useRightDrawerTab } from './useRightDrawerTab';
import { useRightDrawerCatalogRefresh } from './useRightDrawerCatalogRefresh';
import { Colors } from 'styles';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useCaseStudyParamsSuffix } from 'hooks/studyParams';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  hasReportContentChangedState,
  InsertedTemplateDataSource,
  pendingTemplateIdState,
} from '../state';
import { useCurrentUser } from 'hooks/useCurrentUser/legacy';
import { useCurrentCaseReport } from 'hooks/useCurrentCaseReport';
import { RightDrawerHeaderIcon } from './RightDrawerHeaderIcon';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { RightDrawerSectionContent } from './RightDrawerSectionContent';
import { logger } from 'modules/logger';
import type { LazyQueryHookExecOptions } from '@apollo/client';
import type { TemplateInsertionContext } from 'domains/reporter/RichTextEditor/analytics/performance';
import { telemetryContextFor } from 'modules/Apollo/types';
import {
  GetMacroQuery,
  GetMacroQueryVariables,
  GetTemplateQuery,
  GetTemplateQueryVariables,
} from 'generated/graphql';
import { useTemplateInsertionState } from 'hooks/useTemplateInsertionState';
import { useSendModifyDictationMessage } from 'common/Recorder/useRecorder';
import type { RightDrawerCatalogSectionContract } from './catalogSection.types';
import { getRightDrawerBrowsePath, getRightDrawerCreatePath } from './paths';

const HeaderButtonStyle = `
  all: unset;
  display: flex;
  align-items: center;
`;

type Props = {
  drawerHeight: number;
  isSubmittedStatus: boolean;
  procedureSmid: string;
  basePath: string;
  onLoadTemplate?: LoadTemplate;
  onLoadMacro?: (
    options: Partial<LazyQueryHookExecOptions<GetMacroQuery, GetMacroQueryVariables>>
  ) => Promise<void>;
  disabled: boolean;
  disabledDoubleClick: boolean;
  previewItem: Nullable<RightDrawerPreviewItemType>;
  setPreviewItem: (arg1?: Nullable<RightDrawerPreviewItemType>) => void;
  setForceShowPreviewItem: (arg1: boolean) => void;
  activeTab: RightDrawerTabType;
  setActiveTab: (arg1: RightDrawerTabType) => void;
  setIsPreviewLoading: (arg1: boolean) => void;
};

export type LoadTemplate = (
  options?: Partial<LazyQueryHookExecOptions<GetTemplateQuery, GetTemplateQueryVariables>>,
  insertionContext?: TemplateInsertionContext
) => Promise<unknown>;

export const RightDrawerTabSection = memo(
  ({
    drawerHeight,
    isSubmittedStatus,
    procedureSmid,
    basePath,
    onLoadTemplate,
    onLoadMacro,
    disabled,
    disabledDoubleClick,
    previewItem,
    setPreviewItem,
    setForceShowPreviewItem,
    activeTab,
    setActiveTab,
    setIsPreviewLoading,
  }: Props): React.ReactElement => {
    const location = useLocation();
    const { worklistId } = useParams<{ worklistId?: string }>();
    const priorsSuffix = useCaseStudyParamsSuffix(worklistId ?? null);
    const { showCompactHeight } = useCompactUI();
    const [expanded, setExpanded] = useState(true);

    const setPendingTemplateId = useSetRecoilState(pendingTemplateIdState);
    const hasReportContentChanged = useRecoilValue(hasReportContentChangedState);

    const {
      data: userData,
      loading: loadingMe,
      error: errorMe,
    } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });
    const { currentCaseReport } = useCurrentCaseReport();
    const currentWorklistItemSmid = currentCaseReport?.smid;
    const defaultAutoloadTemplateID = userData?.me?.defaultAutoloadTemplate?.id;

    const sendModifyDictationMessage = useSendModifyDictationMessage();

    const availableTabs = DEFAULT_RIGHT_DRAWER_TABS;

    const minimumPicklistsHeight = useMemo(
      () =>
        showCompactHeight ? COMPACT_MINIMUM_PICKLISTS_HEIGHT : DEFAULT_MINIMUM_PICKLISTS_HEIGHT,
      [showCompactHeight]
    );

    const defaultSectionHeight = useMemo(
      () => drawerHeight - minimumPicklistsHeight - RIGHT_DRAWER_FILTER_HEIGHT,
      [drawerHeight, minimumPicklistsHeight]
    );

    const { minSectionHeight, collapseSectionHeight, collapseSectionHeaderHeight } = useMemo(
      () => (showCompactHeight ? COMPACT_HEIGHTS : DEFAULT_HEIGHTS),
      [showCompactHeight]
    );

    const useTabResizableProps = useResizable({
      collapseSize: collapseSectionHeight,
      expandSize: defaultSectionHeight,
      minimumSize: minSectionHeight,
      dimension: 'height',
      initialSize: defaultSectionHeight,
    });

    const {
      componentSize,
      sizes,
      toggleOpenComponent: toggleExpanded,
      setComponentSize,
    } = useTabResizableProps;

    useEffect(() => {
      if (componentSize < 0) {
        setComponentSize(defaultSectionHeight);
      } else {
        setExpanded(sizes?.height != null && sizes.height > collapseSectionHeaderHeight);
      }
    }, [
      sizes?.height,
      collapseSectionHeight,
      collapseSectionHeaderHeight,
      setComponentSize,
      defaultSectionHeight,
      componentSize,
    ]);

    const { setTemplateInsertion } = useTemplateInsertionState();

    const debouncedOnLoadTemplate = useDebouncedCallback(
      ({
        id,
        insertionContext,
        name,
        source,
      }: {
        id: string;
        insertionContext: TemplateInsertionContext;
        name: string;
        source: InsertedTemplateDataSource;
      }) => {
        onLoadTemplate?.(
          {
            variables: { id },
            context: {
              telemetry: {
                ...APOLLO_QUERY_CONTEXT.telemetry,
                fetchPolicy: 'network-only',
              },
            },
          },
          insertionContext
        );
        logger.info(`[RightDrawerTabSection] Template "${id} - ${name}" loaded`, {
          templateId: id,
          templateName: name,
          source,
        });
      },
      ON_LOAD_TEMPLATE_DEBOUNCE
    );

    const templateRightDrawerTabProps = useRightDrawerTab({
      tab: RightDrawerTab.Template,
      procedureSmid,
      skip: errorMe != null || loadingMe,
      setIsPreviewLoading,
      context: APOLLO_QUERY_CONTEXT,
    });

    const macroRightDrawerTabProps = useRightDrawerTab({
      tab: RightDrawerTab.Macro,
      procedureSmid,
      setIsPreviewLoading,
      context: APOLLO_QUERY_CONTEXT,
    });
    const { refetchItems: refetchTemplateItems } = templateRightDrawerTabProps;
    const { refetchItems: refetchMacroItems } = macroRightDrawerTabProps;

    useRightDrawerCatalogRefresh({ refetchTemplateItems, refetchMacroItems });

    const onTemplateRowClick = useCallback(
      async ({ id, name, action }: RightDrawerTableItemType) => {
        if (disabled) {
          return;
        }

        if (previewItem?.id === id && !disabledDoubleClick) {
          if (!isSubmittedStatus && !hasReportContentChanged) {
            if (currentWorklistItemSmid == null) {
              logger.error(
                '[RightDrawerTabSection] No current worklist item smid found. Cannot insert template.'
              );
              return;
            }
            const insertionContext = setTemplateInsertion({
              templateId: id,
              source: InsertedTemplateDataSource.RightDrawerSingleClick,
              worklistItemSmid: currentWorklistItemSmid,
            });
            debouncedOnLoadTemplate({
              id,
              insertionContext,
              name,
              source: InsertedTemplateDataSource.RightDrawerSingleClick,
            });
            setForceShowPreviewItem(false);
          } else {
            setPendingTemplateId({
              templateId: id,
              source: InsertedTemplateDataSource.RightDrawerSingleClick,
            });
          }
        } else {
          const content = await templateRightDrawerTabProps.fetchPreviewContent(id);
          setPreviewItem({ id, name: `Template - ${name}`, content, action });
          setForceShowPreviewItem(true);
        }
      },
      [
        debouncedOnLoadTemplate,
        currentWorklistItemSmid,
        disabled,
        disabledDoubleClick,
        hasReportContentChanged,
        isSubmittedStatus,
        previewItem?.id,
        setForceShowPreviewItem,
        setTemplateInsertion,
        setPendingTemplateId,
        setPreviewItem,
        templateRightDrawerTabProps,
      ]
    );

    const onMacroRowClick = useCallback(
      async ({ id, name, action }: RightDrawerTableItemType) => {
        if (disabled) {
          return;
        }

        if (previewItem?.id === id && !disabledDoubleClick) {
          if (onLoadMacro == null) {
            return;
          }

          await onLoadMacro({
            variables: { id },
            context: {
              telemetry: { ...APOLLO_QUERY_CONTEXT.telemetry, fetchPolicy: 'network-only' },
            },
          });
          sendModifyDictationMessage();
          logger.info(`[RightDrawerTabSection] Macro "${id} - ${name}" loaded`, {
            macroId: id,
            macroName: name,
            source: 'click',
          });
          setForceShowPreviewItem(false);
        } else {
          const content = await macroRightDrawerTabProps.fetchPreviewContent(id);
          setPreviewItem({
            id,
            name: `Macro - ${name}`,
            content,
            action,
          });
          setForceShowPreviewItem(true);
        }
      },
      [
        disabled,
        disabledDoubleClick,
        macroRightDrawerTabProps,
        onLoadMacro,
        previewItem?.id,
        sendModifyDictationMessage,
        setForceShowPreviewItem,
        setPreviewItem,
      ]
    );

    const onTemplateDoubleClick = useCallback(
      async ({ id, name }: RightDrawerTableItemType) => {
        if (disabledDoubleClick) {
          return;
        }

        if (!isSubmittedStatus && !hasReportContentChanged) {
          if (currentWorklistItemSmid == null) {
            logger.error(
              '[RightDrawerTabSection] No current worklist item smid found. Cannot insert template.'
            );
            return;
          }
          const insertionContext = setTemplateInsertion({
            templateId: id,
            source: InsertedTemplateDataSource.RightDrawerDoubleClick,
            worklistItemSmid: currentWorklistItemSmid,
          });
          debouncedOnLoadTemplate({
            id,
            insertionContext,
            name,
            source: InsertedTemplateDataSource.RightDrawerDoubleClick,
          });
        } else {
          setPendingTemplateId({
            templateId: id,
            source: InsertedTemplateDataSource.RightDrawerDoubleClick,
          });
        }
      },
      [
        debouncedOnLoadTemplate,
        currentWorklistItemSmid,
        disabledDoubleClick,
        hasReportContentChanged,
        isSubmittedStatus,
        setTemplateInsertion,
        setPendingTemplateId,
      ]
    );

    const onMacroDoubleClick = useCallback(
      async ({ id, name }: RightDrawerTableItemType) => {
        if (disabledDoubleClick) {
          return;
        }

        if (onLoadMacro == null) {
          return;
        }

        await onLoadMacro({ variables: { id } });
        sendModifyDictationMessage();
        logger.info(`[RightDrawerTabSection] Macro "${id} - ${name}" loaded`, {
          macroId: id,
          macroName: name,
          source: 'double-click',
        });
      },
      [disabledDoubleClick, onLoadMacro, sendModifyDictationMessage]
    );

    const tabIndex = availableTabs.findIndex((tabItem) => tabItem.tab === activeTab);

    const catalogSections = useMemo<Record<RightDrawerTabType, RightDrawerCatalogSectionContract>>(
      () => ({
        [RightDrawerTab.Template]: {
          tab: RightDrawerTab.Template,
          tabProps: templateRightDrawerTabProps,
          onRowClick: onTemplateRowClick,
          onRowDoubleClick: onTemplateDoubleClick,
          starredId: defaultAutoloadTemplateID ?? null,
        },
        [RightDrawerTab.Macro]: {
          tab: RightDrawerTab.Macro,
          tabProps: macroRightDrawerTabProps,
          onRowClick: onMacroRowClick,
          onRowDoubleClick: onMacroDoubleClick,
          starredId: null,
        },
      }),
      [
        defaultAutoloadTemplateID,
        macroRightDrawerTabProps,
        onMacroDoubleClick,
        onMacroRowClick,
        onTemplateDoubleClick,
        onTemplateRowClick,
        templateRightDrawerTabProps,
      ]
    );

    const activeCatalogSection = catalogSections[activeTab];
    return (
      <RightDrawerResizableSection
        currentMaxHeight={defaultSectionHeight}
        minSectionHeight={minSectionHeight}
        expanded={expanded}
        useResizableProps={useTabResizableProps}
        heading={
          <Stack
            alignY="center"
            alignX="between"
            css={{
              flexShrink: 0,
            }}
          >
            <Tabs
              value={tabIndex !== -1 ? tabIndex : 0}
              onChange={(evt, index) => {
                setActiveTab(availableTabs[index].tab);
              }}
              sx={{ padding: 0, minHeight: 'unset' }}
              slotProps={{
                indicator: {
                  sx: {
                    backgroundColor: Colors.blue2,
                    padding: 0,
                  },
                },
              }}
            >
              {availableTabs.map((tabItem) => (
                <Tab
                  key={tabItem.tab}
                  label={tabItem.label}
                  data-testid={`right-drawer-tab-${tabItem.tab}`}
                  disableRipple
                  sx={{
                    minWidth: 0,
                    textTransform: 'none',
                    fontWeight: 400,
                    fontSize: '1.3rem',
                    lineHeight: '2.2rem',
                    color: Colors.gray8,
                    padding: '8px',
                    borderBottom: `1px solid ${Colors.gray5}`,
                    minHeight: 'unset',
                  }}
                />
              ))}
            </Tabs>
            <Stack>
              <span css={HeaderButtonStyle}>
                <>
                  <Link
                    to={getRightDrawerBrowsePath({
                      basePath,
                      activeTab,
                      search: location.search,
                    })}
                    css={HeaderButtonStyle}
                    data-testid={`browse-${activeTab}s`}
                  >
                    <RightDrawerHeaderIcon
                      as={FolderIcon}
                      data-testid="right-drawer-header-icon-browse"
                    />
                  </Link>
                  <Link
                    to={getRightDrawerCreatePath({
                      basePath,
                      activeTab,
                      priorsSuffix,
                    })}
                    css={HeaderButtonStyle}
                    data-testid={`create-${activeTab}s`}
                  >
                    <RightDrawerHeaderIcon
                      as={AddCircleIcon}
                      data-testid="right-drawer-header-icon-add"
                      hovercolor={Colors.green5}
                    />
                  </Link>
                </>
                <RightDrawerHeaderIcon
                  as={expanded ? ExpandLessIcon : ExpandMoreIcon}
                  data-testid="right-drawer-header-icon-expand"
                  onClick={toggleExpanded}
                  onFocus={toggleExpanded}
                />
              </span>
            </Stack>
          </Stack>
        }
      >
        <RightDrawerSectionContent
          expanded={expanded}
          basePath={basePath}
          catalogSection={activeCatalogSection}
        />
      </RightDrawerResizableSection>
    );
  }
);

RightDrawerTabSection.displayName = 'RightDrawerTabSection';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(RightDrawerTabSection);
